import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

import type { SpecType } from "@/api/contracts/specifications";
import { CheckboxField } from "@/components/ui/checkbox-field";
import { CircularProgress } from "@/components/ui/circular-progress";
import { ErrorSnackbar } from "@/components/ui/error-snackbar";
import {
  BlockedMenuItem,
  IconMenuButton,
  IconMenuButtonContent,
  IconMenuButtonTrigger,
  MenuRowDivider,
  MoreVertGlyph,
  PressableMenuItem,
} from "@/components/ui/icon-menu-button";
import { IconMenuContext } from "@/components/ui/icon-menu-context";
import { pressedOnBackdrop } from "@/components/ui/mui-backdrop-press";
import { useUpdateSpecLinePerPlan } from "@/hooks/spec-editor-per-plan";
import {
  useClearSpecLine,
  useClearSpecSelectionItem,
  useCreateSpecSelection,
  useResetSpecLine,
  useResetSpecSelection,
  useUpdateSpecSelectionPaidUpgrade,
} from "@/hooks/spec-editor-selections";
import type { EditorMutationResult } from "@/hooks/spec-editor-cache";
import type { SpecEditorTarget } from "@/hooks/spec-editor-target";
import type { SpecLinesLine, SpecLinesSelection } from "@/hooks/specifications";
import { ERRORS } from "@/lib/spec-editor-errors";
import {
  PER_PLAN_ADD_SELECTIONS_REASON,
  lineMenuRows,
  type LineMenuAction,
  type LineMenuRow,
} from "@/lib/spec-line-menu";
import type { EditorRowKind } from "@/lib/spec-lines-display";

import type { SpecActiveRow } from "./spec-line-dialogs";

// A line row's ⋮ (#810) — the donor's `LineMoreActions` around its
// `ActionMenu` (`SpecEditIsland/components/MainContent/Lines/LineMoreActions/`),
// in the last cell of #809's rows. The parity enumeration on #810 names the
// source line of every fact below.
//
// **The surface** is the shared modal item menu, hung to the trigger's left —
// the donor's MUI `Menu` with `anchorOrigin {top, left}` against
// `transformOrigin {top, right}`, `minWidth 230`, root z 1400 and no scroll
// lock. The rows are `lineMenuRows`' (the gates are a pure rule beside this):
// stock rows, two rules, and the two `LabeledCheckBox` rows, which sit outside
// the arrow-key walk as the donor's `Stack` does — MUI's `MenuList` walks its
// `MenuItem`s only.
//
// **What each row does** (`onAction`, `LineMoreActions.tsx:128-241`):
//
//   - `Add Selections` and the two resets write at once and hold — the row
//     spins, the menu stays open, and it closes when the answer arrives,
//     before the answer lands; open over its snackbar on a failure (#1084:
//     `Reset to Base` / `Reset to Core`, the line's whole from a single or
//     header row, the selection's from a selection row);
//   - the dialog rows ask the page's host for their dialog (#807), which the
//     donor mounted per row; `Delete`'s confirm clears the Active selection on
//     success, there;
//   - `Clear` (the line's or the selection's) writes at once and waits for the
//     server (a named break: not optimistic);
//   - the boxes close the menu, then write optimistically.
//
// **The Active selection** follows the donor's: every row but Clear, Delete
// and Per Plan sets it to this row once it acts; Clear and Per Plan set it at
// the click, where the donor's guess re-emitted it — by ids, so the cache's own
// answer or rollback is what the row shows (#810 Assumption 4); Delete leaves
// it alone. **Per Plan's set clears the item**, either way it toggles — the
// donor's emit is the selection with `itemId: null` (`LineMoreActions/
// actions.ts:448-458`, #861 D3 a), so the island drops its pinned card while
// the cache keeps the item behind the row's per-plan text — and a failed write
// puts the plain row back, only while that set is still the Active selection
// (S7 on #903): from the write's promise, which outlives the menu's row.
//
// **And every click the donor's row would have heard.** The donor's menu and
// dialogs are portalled but are the row's React children, and only a chosen
// row stops its click, so a click-away, a press on a held row, a rule or a
// box's blank space all make the row active too (measured, #810 enumeration
// 20–22). #809's slot stops every click for this cell, so the menu says so
// itself: its chosen rows stop their clicks, and what bubbles past them — or
// lands on the backdrop — sets the row. A box's own click is the one exception
// in the other direction: the donor's row heard it *before* the box's change
// emitted (React runs a click's listeners ahead of its change's), so the box's
// set is the click's last; the box here changes inside its click, so the row
// is told nothing and the box sets it itself.

/** The dialogs a row's menu opens (#807); the page hosts them. */
export type SpecLineMenuDialog =
  "edit-line" | "duplicate-line" | "delete-line" | "edit-selection" | "delete-selection";

export interface SpecLineDialogRequest {
  dialog: SpecLineMenuDialog;
  line: SpecLinesLine;
  selection: SpecLinesSelection | undefined;
}

export interface SpecLineMenuProps {
  line: SpecLinesLine;
  /** The row's selection — a header row's is its first shown one (#809). */
  selection: SpecLinesSelection | undefined;
  kind: EditorRowKind;
  /** The page's Active selection setter — an updater reads the one it holds. */
  setActive: Dispatch<SetStateAction<SpecActiveRow | null>>;
  specType: SpecType;
  target: SpecEditorTarget;
  /** Asks the page's host for a dialog on this row. */
  onOpenDialog: (request: SpecLineDialogRequest) => void;
}

/** `slotProps.paper.sx.minWidth` (`ActionMenu.tsx:120-125`). */
const MENU_MIN_WIDTH_PX = 230;

const DIALOG_OF: Partial<Record<LineMenuAction, SpecLineMenuDialog>> = {
  EDIT_LINE: "edit-line",
  DUPLICATE_LINE: "duplicate-line",
  DELETE_LINE: "delete-line",
  EDIT_SELECTION: "edit-selection",
  DELETE_SELECTION: "delete-selection",
};

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

export function SpecLineMenu({
  line,
  selection,
  kind,
  setActive,
  specType,
  target,
  onOpenDialog,
}: SpecLineMenuProps) {
  const [open, setOpen] = useState(false);
  // The family closes through its own context — that is what pins the exit
  // and hands the keyboard back — so the rows hand their `close` up here for
  // the closes no row's handler runs: a held row's answer.
  const closeMenu = useRef<() => void>(() => {});

  // A held row's menu closes as the answer arrives, ahead of it landing.
  const answered = { onAnswer: () => closeMenu.current() };
  const create = useCreateSpecSelection(answered);
  const resetLine = useResetSpecLine({
    onAnswer: (result) => {
      closeMenu.current();
      onLineReset(result);
    },
  });
  const resetSelection = useResetSpecSelection(answered);
  const clearLine = useClearSpecLine();
  const clearSelection = useClearSpecSelectionItem("menu");
  const perPlan = useUpdateSpecLinePerPlan();
  const paidUpgrade = useUpdateSpecSelectionPaidUpgrade();

  const row: SpecActiveRow = { lineId: line.id, selectionId: selection?.id ?? null };
  const makeActive = () => setActive(row);
  // Line Reset restores the line's selections as new rows, which can redraw
  // this row away before the answer lands (#1084). The donor's line keeps its
  // id and stays Active; here the pair follows the row's selection onto its
  // lineage's copy, else the line's first.
  const onLineReset = (result: EditorMutationResult) => {
    const reset = result.lines?.find(({ id }) => id === line.id);
    if (!reset) return makeActive();
    const copy =
      reset.selections.find(({ lineage }) => lineage !== null && lineage === selection?.lineage) ??
      reset.selections[0];
    setActive({ lineId: line.id, selectionId: copy?.id ?? null });
  };

  // A click-away closes the menu (the family's rule) and is a click on the
  // donor's row: its backdrop was the row's React child.
  useBackdropRelease(open, makeActive);

  const onAction = (verb: LineMenuAction, close: () => void) => {
    const dialog = DIALOG_OF[verb];
    if (dialog) {
      onOpenDialog({ dialog, line, selection });
      if (verb !== "DELETE_LINE" && verb !== "DELETE_SELECTION") makeActive();
      return;
    }
    const lineTarget = { ...target(), lineId: line.id };
    switch (verb) {
      case "ADD_SELECTION":
        create.mutate(lineTarget, { onSettled: makeActive });
        return;
      case "RESET_LINE":
        // A failure leaves the row drawn: it is made Active as it stands.
        resetLine.mutate(lineTarget, { onError: makeActive });
        return;
      case "RESET_SELECTION":
        if (!selection) return;
        resetSelection.mutate(
          { ...lineTarget, selectionId: selection.id },
          { onSettled: makeActive },
        );
        return;
      case "CLEAR_LINE":
        makeActive();
        clearLine.mutate(lineTarget);
        return;
      case "CLEAR_SELECTION":
        if (!selection) return;
        makeActive();
        clearSelection.mutate({ ...lineTarget, selectionId: selection.id });
        return;
      case "TOGGLE_PER_PLAN": {
        close();
        const cleared: SpecActiveRow = { ...row, itemCleared: true };
        setActive(cleared);
        // The snackbar and the box's rollback are the hook's; the item's
        // return is this set's alone, and only while it lasts.
        perPlan
          .mutateAsync({ ...lineTarget, isPerPlan: !line.perPlan })
          .catch(() => setActive((now) => (now === cleared ? { ...row } : now)));
        return;
      }
      case "TOGGLE_PAID_UPGRADE":
        if (!selection) return;
        close();
        paidUpgrade.mutate({
          ...lineTarget,
          selectionId: selection.id,
          isPaidUpgrade: !selection.isPaidUpgrade,
        });
        makeActive();
        return;
    }
  };

  const rows = lineMenuRows({ kind, line, selection, specType });

  return (
    // The row's click, for whatever bubbles out of the menu unstopped — but a
    // box's, which sets the row itself (see the module comment).
    <div
      className="contents"
      onClick={(event) => {
        if ((event.target as Element).closest("[data-line-menu-box]")) return;
        makeActive();
      }}
    >
      <IconMenuButton onOpenChange={setOpen}>
        <IconMenuButtonTrigger
          label="More options"
          box="default"
          data-testid={`line-${line.id}-more-menu`}
          disabled={kind === "SELECTION_ONLY" && line.deleted}
          // `handleMenu` stops it: opening never moves the Active selection.
          onClick={stop}
        >
          <MoreVertGlyph />
        </IconMenuButtonTrigger>
        <IconMenuButtonContent
          minWidthPx={MENU_MIN_WIDTH_PX}
          side="left"
          align="start"
          rowVariant="body1"
          // `disableScrollLock` (`ActionMenu.tsx:117`): the page keeps its wheel.
          scrollLock={false}
          // `slotProps.root.sx.zIndex` (`ActionMenu.tsx:118-120`).
          className="z-[1400]"
        >
          <LineMenuRows
            rows={rows}
            pending={{
              ADD_SELECTION: create.isPending,
              RESET_LINE: resetLine.isPending,
              RESET_SELECTION: resetSelection.isPending,
            }}
            closeRef={closeMenu}
            onAction={onAction}
          />
        </IconMenuButtonContent>
      </IconMenuButton>
      <ErrorSnackbar open={create.isError} message={ERRORS.ADD_SELECTION} onClose={create.reset} />
      <ErrorSnackbar
        open={resetLine.isError}
        message={ERRORS.RESET_LINE}
        onClose={resetLine.reset}
      />
      <ErrorSnackbar
        open={resetSelection.isError}
        message={ERRORS.RESET_SELECTION}
        onClose={resetSelection.reset}
      />
      <ErrorSnackbar
        open={clearLine.isError}
        message={ERRORS.CLEAR_LINE}
        onClose={clearLine.reset}
      />
      <ErrorSnackbar
        open={clearSelection.isError}
        message={ERRORS.CLEAR_SELECTION}
        onClose={clearSelection.reset}
      />
      <ErrorSnackbar
        open={perPlan.isError}
        message={ERRORS.UPDATE_LINE_IS_PER_PLAN}
        onClose={perPlan.reset}
      />
      <ErrorSnackbar
        open={paidUpgrade.isError}
        message={ERRORS.UPDATE_LINE_IS_PAID_UPGRADE}
        onClose={paidUpgrade.reset}
      />
    </div>
  );
}

/**
 * Calls `onRelease` for a press and release that both land on the page while
 * the menu is open — the gesture the family's `useBackdropPressDismiss` closes
 * the menu on, read the same way (the primary button, one pointer).
 *
 * On `window` in the capture phase, so it runs ahead of the family's
 * `document` listener: that one closes the menu inside the same `pointerup`,
 * and a listener keyed on the open state would be removed mid-dispatch, before
 * the browser reached it.
 */
function useBackdropRelease(open: boolean, onRelease: () => void) {
  const latest = useRef(onRelease);
  useEffect(() => {
    latest.current = onRelease;
  });
  useEffect(() => {
    if (!open) return;
    let pressedBy: number | null = null;
    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      pressedBy = pressedOnBackdrop(event.target) ? event.pointerId : null;
    };
    const onPointerUp = (event: PointerEvent) => {
      if (event.button !== 0 || pressedBy !== event.pointerId) return;
      pressedBy = null;
      if (pressedOnBackdrop(event.target)) latest.current();
    };
    const onPointerCancel = (event: PointerEvent) => {
      if (pressedBy === event.pointerId) pressedBy = null;
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointerup", onPointerUp, true);
    window.addEventListener("pointercancel", onPointerCancel, true);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointerup", onPointerUp, true);
      window.removeEventListener("pointercancel", onPointerCancel, true);
    };
  }, [open]);
}

/**
 * The paper's rows, drawn inside the menu so they can close it through the
 * family's context — and hand that close up for a held row's answer.
 */
function LineMenuRows({
  rows,
  pending,
  closeRef,
  onAction,
}: {
  rows: LineMenuRow[];
  /**
   * The rows that write at once and hold the menu open until their answer,
   * each with whether its write is in the air.
   */
  pending: Partial<Record<LineMenuAction, boolean>>;
  closeRef: React.RefObject<() => void>;
  onAction: (verb: LineMenuAction, close: () => void) => void;
}) {
  const menu = useContext(IconMenuContext);
  const close = useMemo(() => menu?.close ?? (() => {}), [menu]);
  useEffect(() => {
    closeRef.current = close;
  }, [close, closeRef]);

  const boxes = rows.filter((row) => row.type === "checkbox");
  const listed = rows.filter((row) => row.type !== "checkbox");

  return (
    <>
      {listed.map((row, index) => {
        if (row.type === "divider") return <MenuRowDivider key={`rule-${index}`} />;
        if (row.type !== "action") return null;
        if (row.disabledReason) {
          return (
            <BlockedMenuItem
              key={row.action}
              label={row.label}
              reason={PER_PLAN_ADD_SELECTIONS_REASON}
              side="top"
              testIdPrefix="line-menu"
              tooltipTestId={
                row.action === "ADD_SELECTION" ? "tooltip-disable-selection" : undefined
              }
            />
          );
        }
        const held = row.action in pending;
        const spinning = pending[row.action] === true;
        return (
          <PressableMenuItem
            key={row.action}
            data-testid={`line-menu-${row.action}`}
            disabled={spinning}
            // A chosen row is the one click the donor's `onAction` stops.
            onClick={stop}
            onSelect={(event) => {
              // A held row keeps the menu open until its answer.
              if (held) event.preventDefault();
              onAction(row.action, close);
            }}
          >
            {row.label}
            {spinning ? (
              // `CircularProgress size={20} sx={{ ml: 2 }}` (`ActionMenu.tsx:150`):
              // 16px after the label, the item's 8px gap with its own 8px; in
              // the primary blue, which the item's svg rule would grey.
              <CircularProgress size={20} className="ml-2 [&_svg]:text-inherit!" />
            ) : null}
          </PressableMenuItem>
        );
      })}
      {/* The boxes' `Stack` — a column, outside the walk, drawn whether or not
          it holds anything (`ActionMenu.tsx:222-248`). */}
      <div className="flex flex-col items-start">
        {boxes.map((row) =>
          row.type === "checkbox" ? (
            <div key={row.action} className="contents" data-line-menu-box>
              <CheckboxField
                data-testid={`line-menu-${row.action}`}
                label={row.label}
                checked={row.checked}
                onChange={() => onAction(row.action, close)}
                labelPlacement="start"
                // `controlLabelStyle {justifyContent: 'space-between', width:
                // '100%'}` on the label, `sx {mr: 2}` on the box.
                className="w-full justify-between"
                boxClassName="mr-4"
              />
            </div>
          ) : null,
        )}
      </div>
    </>
  );
}
